import { waitForElement } from "~/utils/domUtils";

/**
 * Focus the content area (TipTap editor) in a timeline item.
 * Used when entering edit mode via "e" key in display mode to focus directly on the editable content.
 * Uses MutationObserver to wait for the element to appear after mode change renders.
 */
export const focusContentInItem = async (
  containerElement: HTMLElement,
  itemId: string,
): Promise<void> => {
  const selector = `[data-timeline-item-id="${itemId}"] .indigo-timeline-item__markdown-content-editor [contenteditable="true"]`;
  const contentEditor = await waitForElement(selector, containerElement);

  if (contentEditor) {
    // Small delay after element appears to ensure it's fully initialized
    requestAnimationFrame(() => {
      contentEditor.focus();
    });
  }
};

/**
 * Focus the content editor in an already-rendered timeline item.
 * Used when pressing "e" key on a focused item in edit mode.
 * Unlike focusContentInItem, this doesn't wait for the element since the item is already rendered.
 */
export const focusContentEditorInItem = (
  containerElement: HTMLElement,
  itemId: string,
): void => {
  const contentEditor = containerElement.querySelector(
    `[data-timeline-item-id="${itemId}"] .indigo-timeline-item__markdown-content-editor [contenteditable="true"]`,
  ) as HTMLElement | null;

  if (contentEditor) {
    contentEditor.dispatchEvent(
      new CustomEvent("tiptap-focus-start", { bubbles: false }),
    );
  }
};

/** Use the rendered triggers so keyboard and mouse share popover state. */
export const closeSuggestionPopovers = (
  container: HTMLElement | null,
): boolean => {
  const triggers = container?.querySelectorAll<HTMLElement>(
    '[data-suggestion-trigger][aria-expanded="true"]',
  );
  triggers?.forEach((trigger) => trigger.click());
  return !!triggers?.length;
};

export const toggleSuggestionPopoverInItem = (
  container: HTMLElement | null,
  itemId: string,
): boolean => {
  const trigger = container?.querySelector<HTMLElement>(
    `[data-timeline-item-id="${itemId}"] [data-suggestion-trigger]`,
  );
  if (!trigger) return false;
  if (trigger.getAttribute("aria-expanded") !== "true")
    closeSuggestionPopovers(container);
  trigger.click();
  return true;
};
